import { ArrowLeft, ChevronRight, Instagram } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { FacebookIcon, ThreadsIcon, TikTokIcon } from "@/components/icons"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"

interface MobileSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface MenuItem {
  label: string
  route: string
}

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const logout = useAuthStore(s => s.logout)

  const menuItems: MenuItem[] = [
    { label: t("sidebar.nav.myRituality"), route: "/my-rituality" },
    { label: t("sidebar.nav.learnAndPractice"), route: "/learn-and-practice" },
    { label: t("sidebar.nav.connect"), route: "/connect" },
    { label: t("sidebar.nav.chat"), route: "/chats" },
    { label: t("sidebar.nav.blog"), route: "/blog" },
    { label: t("sidebar.nav.settings"), route: "/settings" },
  ]

  const handleNavigate = (route: string) => {
    navigate(route)
    onOpenChange(false)
  }

  const handleLogout = () => {
    logout()
    onOpenChange(false)
  }

  const handleSocialClick = (platform: string) => {
    const urls: Record<string, string> = {
      tiktok: import.meta.env.VITE_SOCIAL_TIKTOK_URL,
      instagram: import.meta.env.VITE_SOCIAL_INSTAGRAM_URL,
      facebook: import.meta.env.VITE_SOCIAL_FACEBOOK_URL,
      threads: import.meta.env.VITE_SOCIAL_THREADS_URL,
    }
    window.open(urls[platform], "_blank", "noopener,noreferrer")
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        hideCloseButton
        className="w-full sm:w-[350px] bg-[#020202] p-0 border-r border-[#2A2A2A] flex flex-col justify-between h-full gap-20 overflow-y-auto"
      >
        <div>
          <div className="h-[48px] flex items-center justify-center relative border-b border-[#2A2A2A] shrink-0">
            <button
              onClick={() => onOpenChange(false)}
              className="absolute left-0 w-[48px] h-[48px] flex items-center justify-center text-[#F2F0EB]/90 hover:text-[#F2F0EB] transition-colors"
              aria-label="Close menu"
            >
              <ArrowLeft size={20} />
            </button>
            <span className="text-[14px] font-semibold tracking-wide uppercase text-[#F2F0EB]">
              {t("sidebar.menu")}
            </span>
          </div>

          <nav>
            <ul className="divide-y divide-[#2A2A2A] border-b border-[#2A2A2A]">
              {menuItems.map(item => (
                <li key={item.route}>
                  <button
                    onClick={() => handleNavigate(item.route)}
                    className="w-full flex items-center justify-between px-5 py-3.75 h-[54px] text-[#F2F0EB]/90 hover:text-[#F2F0EB] hover:bg-[#F2F0EB]/5 transition-colors"
                  >
                    <span className="text-[14px] font-semibold tracking-wide uppercase font-montserrat">
                      {item.label}
                    </span>
                    <ChevronRight size={16} className="text-[#F2F0EB]/90" />
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between px-5 py-3.75 h-[54px] text-[#C86757] hover:bg-[#C86757]/10 transition-colors"
                >
                  <span className="text-[14px] font-semibold tracking-wide uppercase font-montserrat">
                    {t("sidebar.logOut")}
                  </span>
                  <ChevronRight size={16} className="text-[#C86757]" />
                </button>
              </li>
            </ul>
          </nav>
        </div>

        <div className="px-5 pb-10 shrink-0 space-y-5">
          <div className="flex items-center gap-3.75">
            <div className="text-sm text-[#F2F0EB66] leading-6">{t("sidebar.followUs")}</div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => handleSocialClick("tiktok")}
                className="w-9 h-9 rounded-full bg-[#F2F0EB0D] border border-[#F2F0EB26] flex items-center justify-center hover:bg-[#F2F0EB]/10 transition-colors"
                aria-label="TikTok"
              >
                <TikTokIcon size={14} className="w-4 h-4" color="#F2F0EB" />
              </button>
              <button
                onClick={() => handleSocialClick("instagram")}
                className="w-9 h-9 rounded-full bg-[#F2F0EB0D] border border-[#F2F0EB26] flex items-center justify-center hover:bg-[#F2F0EB]/10 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={14} className="w-4 h-4" color="#F2F0EB" />
              </button>
              <button
                onClick={() => handleSocialClick("facebook")}
                className="w-9 h-9 rounded-full bg-[#F2F0EB0D] border border-[#F2F0EB26] flex items-center justify-center hover:bg-[#F2F0EB]/10 transition-colors"
                aria-label="Facebook"
              >
                <FacebookIcon size={14} className="w-4 h-4" color="#F2F0EB" />
              </button>
              <button
                onClick={() => handleSocialClick("threads")}
                className="w-9 h-9 rounded-full bg-[#F2F0EB0D] border border-[#F2F0EB26] flex items-center justify-center hover:bg-[#F2F0EB]/10 transition-colors"
                aria-label="Threads"
              >
                <ThreadsIcon size={14} className="w-4 h-4" color="#F2F0EB" />
              </button>
            </div>
          </div>
          <p className="text-sm text-[#F2F0EB80]">{t("sidebar.copyright")}</p>
        </div>
      </SheetContent>
    </Sheet>
  )
}
